/**
 * Slab utility functions - extracted to break circular dependency
 * between floor.ts and slab.ts
 */
import type { FoundationCondition } from "building-types/foundations/foundationEnums";
import { SlabType } from "building-types/foundations/foundationEnums";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import {
  isInteriorFoundationCondition,
  isBasementFoundationCondition,
  isGarageFoundationCondition,
  isPorchFoundationCondition,
} from "doc/utils/foundationSystemTools";

export function slabTypeAtRoomClass(roomClass: RoomClass, settings: SlabSettings): SlabType {
  switch (roomClass) {
    case RoomClass.HOUSE:
      return settings.interiorSlabType;
    case RoomClass.BASEMENT:
      return settings.basementSlabType;
    case RoomClass.GARAGE:
      return settings.garageSlabType;
    case RoomClass.PORCH:
    case RoomClass.PATIO:
      return settings.porchSlabType;
    case RoomClass.DECK:
    case RoomClass.EXTERIOR:
    default:
      return SlabType.NONE;
  }
}

export function slabDepthAtRoomClass(roomClass: RoomClass, settings: SlabSettings): number {
  switch (roomClass) {
    case RoomClass.HOUSE:
      return settings.interiorSlabType !== SlabType.NONE ? settings.interiorSlabDepth : 0;
    case RoomClass.BASEMENT:
      return settings.basementSlabType !== SlabType.NONE ? settings.basementSlabDepth : 0;
    case RoomClass.GARAGE:
      return settings.garageSlabDepth;
    case RoomClass.PORCH:
    case RoomClass.PATIO:
      return settings.porchSlabDepth;
    case RoomClass.DECK:
    case RoomClass.EXTERIOR:
    default:
      return 0;
  }
}

/**
 * @TODO - this function needs to be updated to use the floor path and not the foundation path.
 * These slab types are currentlyderived from the slab settings and foundation path conditions.
 * Floor path should have the knowledge of these slabs and foundation path needs to drill into the
 * floor path to get the slab depth.
 */
export function slabTypeAtFoundationPath(
  { condition }: { condition: FoundationCondition },
  settings: SlabSettings | undefined,
) {
  if (!settings) {
    return SlabType.NONE;
  }
  if (isInteriorFoundationCondition[condition]) {
    return settings.interiorSlabType;
  } else if (isBasementFoundationCondition[condition]) {
    return settings.basementSlabType;
  } else if (isGarageFoundationCondition[condition]) {
    return settings.garageSlabType;
  } else if (isPorchFoundationCondition[condition]) {
    return settings.porchSlabType;
  } else {
    return SlabType.NONE;
  }
}

/**
 * @TODO - this function needs to be updated to use the floor path and not the foundation path.
 * These slab depths are currently derived from the slab settings and foundation path conditions.
 * Floor path should have the knowledge of these slabs and foundation path needs to drill into the
 * floor path to get the slab depth.
 */
export function slabDepthAtFoundationPath(
  { condition }: { condition: FoundationCondition },
  settings: SlabSettings | undefined,
) {
  if (!settings) {
    return 0;
  }
  if (isInteriorFoundationCondition[condition] && settings.interiorSlabType !== SlabType.NONE) {
    return settings.interiorSlabDepth;
  } else if (isBasementFoundationCondition[condition] && settings.basementSlabType !== SlabType.NONE) {
    return settings.basementSlabDepth;
  } else if (isGarageFoundationCondition[condition]) {
    return settings.garageSlabDepth;
  } else if (isPorchFoundationCondition[condition]) {
    return settings.porchSlabDepth;
  } else {
    return 0;
  }
}
